<template>
  <div>
    <div class="answer-radio-block">
      <VRadioGroup
        :value="questionType"
        @change="changeType($event)"
        hide-details
        class="pt-0"
      >
        <VRadio
          :value="testQuestionTypes.FREE"
          :label="$t('noCorrectAnswer')"
          hide-details
          class="mb-3"
        />
        <VRadio
          :value="testQuestionTypes.TEXT"
          :label="$t('showTheCorrectAnswer')"
          hide-details
          class="mb-3"
        />
      </VRadioGroup>
    </div>
    <div v-if="isTextType" class="answer-block">
      <VTextarea
        v-model="currentAnswer.answer"
        required
        outlined
        dense
        rows="4"
        type="text"
        class="answer-textfield"
        :hint="`${$t('charactersLimit')} - 255`"
        :counter="255"
        :placeholder="currentAnswer.isCorrect ? $t('enterTheCorrectAnswer') : $t('enterTheAnswer')"
        :error-messages="answerErrors"
        @input="saveAnswer"
      />
    </div>
  </div>
</template>

<script>
import {
  toRefs, ref, computed, onMounted, watch,
} from '@vue/composition-api';
import { testQuestionTypes } from '@/enums';
import { useI18n } from 'vue-i18n-composable';

export default {
  name: 'TestQuestionTextType',
  props: {
    questionType: {
      type: String,
      required: true,
    },
    answers: {
      type: Array,
      required: false,
      default: null,
    },
    showValidationMessagesGlobal: {
      type: Boolean,
      required: true,
    },
  },
  setup(props, { emit }) {
    const { answers, showValidationMessagesGlobal, questionType } = toRefs(props);
    const { t } = useI18n();

    const isTextType = computed(() => questionType.value === testQuestionTypes.TEXT);
    const isFreeType = computed(() => questionType.value === testQuestionTypes.FREE);

    const currentAnswer = ref(answers.value[0]);

    const isEmptyAnswer = computed(
      () => isTextType.value && (!currentAnswer.value.answer || !currentAnswer.value.answer.trim()),
    );
    const isEmptyAnswerByUser = computed(() => isTextType.value && currentAnswer.value.answer === '');
    const isAnswerTooLong = computed(() => isTextType.value && currentAnswer.value.answer.length > 255);

    const showValidationMessages = ref(false);

    const isValid = computed(() => {
      return !(isEmptyAnswer.value || isAnswerTooLong.value);
    });

    function saveAnswer(initial = false) {
      if (isFreeType.value) {
        return;
      }

      currentAnswer.value.isValid = isValid.value;
      if (initial === true) {
        emit('initialChangeAnswers', [currentAnswer.value]);
        return;
      }
      emit('changeAnswers', [currentAnswer.value]);
    }

    const answerErrors = computed(() => {
      const errors = [];

      if (isEmptyAnswer.value && (showValidationMessages.value || isEmptyAnswerByUser.value)) {
        errors.push(t('requiredField'));
      }

      if (currentAnswer.value.answer && isAnswerTooLong.value) {
        errors.push(`${t('charactersLimit')} - ${255}`);
      }

      return errors;
    });

    watch(showValidationMessagesGlobal, (flag) => {
      if (!flag) {
        return;
      }

      showValidationMessages.value = true;
    });

    onMounted(() => {
      saveAnswer(true);
    });
    function changeType(e) {
      emit('changeQuestionType', e);
    }

    return {
      saveAnswer,
      answerErrors,
      currentAnswer,
      testQuestionTypes,
      isTextType,
      changeType,
    };
  },
};
</script>

<style lang="scss" scoped>
.answer-block {
  display: flex;
  width: 75%;
  @media screen and (max-width: 900px) {
    width: 100%;
  }
}
.answer-textfield {
  @media screen and (max-width: 599px) {
    width: 80%;
  }
}

.v-input--radio-group::v-deep {
  .v-input__control {
    .v-input__slot {
      .v-input--radio-group__input {
        .v-radio {
          max-width: fit-content;
          .v-label {
            color: #000;
          }
        }
      }
    }
  }
}
</style>

<i18n locale="en" lang="yaml">
  charactersLimit: The maximum number of characters is
  enterTheCorrectAnswer: 'Enter the correct answer*'
  enterTheAnswer: 'Enter the answer*'
  noCorrectAnswer: No correct answer
  showTheCorrectAnswer: Show the correct answer
  requiredField: This field is required
</i18n>

<i18n locale="ru" lang="yaml">
  charactersLimit: Максимальное количество символов
  enterTheCorrectAnswer: 'Введите правильный ответ*'
  enterTheAnswer: 'Введите ответ*'
  noCorrectAnswer: Нет правильного ответа
  showTheCorrectAnswer: Укажите правильный ответ
  requiredField: Обязательное поле для заполнения
</i18n>
